import {
  InterfaceSectionName,
} from '@uniswap/analytics-events'
import { Currency, CurrencyAmount, Token } from '@uniswap/sdk-core'
import { useToggleAccountDrawer } from 'components/AccountDrawer/MiniPortfolio/hooks'
import { ButtonError, ButtonLight, ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import { ConfirmSwapModal } from 'components/ConfirmSwapModal'
import SwapCurrencyInputPanel from 'components/CurrencyInputPanel/SwapCurrencyInputPanel'
import { Field } from 'components/swap/constants'
import { ArrowContainer, ArrowWrapper, OutputSwapSection, SwapSection } from 'components/swap/styled'
import { CHAIN_INFO, useIsSupportedChainId } from 'constants/chains'
import usePermit2Allowance from 'hooks/usePermit2Allowance'
import usePrevious from 'hooks/usePrevious'
import { SwapResult, useMintCallback } from 'hooks/useSwapCallback'
import { Trans } from 'i18n'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown } from 'react-feather'
import { Text } from 'rebass'
import { useAppSelector } from 'state/hooks'
import { InterfaceTrade} from 'state/routing/types'
import { useSwapActionHandlers, useSwapAndLimitContext, useSwapContext } from 'state/swap/hooks'
import { useTheme } from 'styled-components'
import { didUserReject } from 'utils/swapErrorToUserReadableMessage'

import useSelectChain from 'hooks/useSelectChain'
import { useAccount } from 'wagmi'
import { useWeb3React } from '@web3-react/core'
import useCurrencyBalance from 'lib/hooks/useCurrencyBalance'



interface SwapFormProps {
  disableTokenInputs?: boolean
  initialInputCurrency: Currency
  initialOutputCurrency: Currency
}

export function SwapForm({ disableTokenInputs = false, initialInputCurrency,  initialOutputCurrency}: SwapFormProps) {
  const { isDisconnected, chainId: connectedChainId } = useAccount()

  const { chainId, prefilledState, currencyState } = useSwapAndLimitContext()
  const isSupportedChain = useIsSupportedChainId(chainId)
  const { swapState, setSwapState, derivedSwapInfo } = useSwapContext()
  const { typedValue, independentField } = swapState


  const theme = useTheme()

  // toggle wallet when disconnected
  const toggleWalletDrawer = useToggleAccountDrawer()

  const {
    trade: { state: tradeState, trade, swapQuoteLatency },
    allowedSlippage,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError,
    outputFeeFiatValue,
    inputTax,
    outputTax,
  } = derivedSwapInfo

  const [inputTokenHasTax, outputTokenHasTax] = useMemo(
    () => [!inputTax.equalTo(0), !outputTax.equalTo(0)],
    [inputTax, outputTax]
  )


  


  const { onSwitchTokens, onCurrencySelection, onUserInput } = useSwapActionHandlers()
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )


  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapError, swapResult }, setSwapFormState] = useState<{
    showConfirm: boolean
    tradeToConfirm?: InterfaceTrade
    swapError?: Error
    swapResult?: SwapResult
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    swapError: undefined,
    swapResult: undefined,
  })

  const previousConnectedChainId = usePrevious(connectedChainId)
  const previousPrefilledState = usePrevious(prefilledState)
  useEffect(() => {
    const chainChanged = previousConnectedChainId && previousConnectedChainId !== connectedChainId
    const prefilledInputChanged =
      previousPrefilledState?.inputCurrency &&
      !prefilledState.inputCurrency?.equals(previousPrefilledState.inputCurrency)
    const prefilledOutputChanged =
      previousPrefilledState?.outputCurrency &&
      !prefilledState?.outputCurrency?.equals(previousPrefilledState.outputCurrency)

    if (chainChanged || prefilledInputChanged || prefilledOutputChanged) {
      // reset local state
      setSwapFormState({
        tradeToConfirm: undefined,
        swapError: undefined,
        showConfirm: false,
        swapResult: undefined,
      })
    }
  }, [
    connectedChainId,
    prefilledState.inputCurrency,
    prefilledState?.outputCurrency,
    previousConnectedChainId,
    previousPrefilledState,
  ])

  const formattedAmounts = useMemo(
    () => ({
      [independentField]: typedValue,
      [dependentField]: typedValue,
    }),
    [dependentField, independentField, typedValue]
  )


  const { account } = useWeb3React()
  const maxInputCurrencyBalance = useCurrencyBalance(account ?? undefined, initialInputCurrency ?? undefined) // will force to allow spending on all current balance or max

  const allowance = usePermit2Allowance(
      (maxInputCurrencyBalance?.currency.isToken
        ? (maxInputCurrencyBalance as CurrencyAmount<Token>)
        : undefined)
  )


  const handleContinueToReview = useCallback(() => {
    setSwapFormState({
      tradeToConfirm: undefined,
      swapError: undefined,
      showConfirm: true,
      swapResult: undefined,
    })
  }, [formattedAmounts[Field.OUTPUT]])

  const clearSwapState = useCallback(() => {
    setSwapFormState((currentState) => ({
      ...currentState,
      swapError: undefined,
      swapResult: undefined,
    }))
  }, [])

 
  // the callback to execute the mint
  const mintCallback = useMintCallback(
    formattedAmounts[Field.OUTPUT]
  )

  const handleMint = useCallback(() => {
    if (!mintCallback) {
      return
    }
   
    mintCallback()
      .then((result) => {
        setSwapFormState((currentState) => ({
          ...currentState,
          swapError: undefined,
          swapResult: result,
        }))
      })
      .catch((error) => {
        setSwapFormState((currentState) => ({
          ...currentState,
          swapError: error,
          swapResult: undefined,
        }))
      })
  }, [mintCallback])


  const handleConfirmDismiss = useCallback(() => {
    setSwapFormState((currentState) => ({ ...currentState, showConfirm: false }))
    // If swap had a temporary router preference override, we want to reset it
    setSwapState((state) => ({ ...state, routerPreferenceOverride: undefined }))
    // If there was a swap, we want to clear the input
    if (swapResult) {
      onUserInput(Field.INPUT, '')
    }
  }, [onUserInput, setSwapState, swapResult])


  const inputCurrencyNumericalInputRef = useRef<HTMLInputElement>(null)



  const inputCurrency = currencies[Field.INPUT] ?? undefined

  const selectChain = useSelectChain()

  const switchingChain = useAppSelector((state) => state.wallets.switchingChain)
  const targetChain = switchingChain ? switchingChain : undefined
  const switchingChainIsSupported = useIsSupportedChainId(targetChain)

  return (
    <>
      { showConfirm && (
        <ConfirmSwapModal
          inputCurrency={initialInputCurrency}
          originalTrade={undefined}
          onCurrencySelection={onCurrencySelection}
          nutsAmount={formattedAmounts[Field.OUTPUT]}
          swapResult={swapResult}
          allowedSlippage={allowedSlippage}
          clearSwapState={clearSwapState}
          //onConfirm={handleSwap} // inside: useSwapCallback : useUniverscalRouterSwapCallback with permit and estimateGas which fails due to our change in usePermit2Allowance (shouldNeedPermit = false) 
          onConfirm={handleMint}
          allowance={allowance}
          swapError={swapError}
          onDismiss={handleConfirmDismiss}
        />
      )}
    
      <div style={{ display: 'relative' }}>
        <SwapSection>
            <SwapCurrencyInputPanel
              label={<Trans>Burn</Trans>}
              disabled={true}
              onUserInput={() => {}}
              value={(typeof parseFloat(formattedAmounts[Field.OUTPUT]) === "number" && !Number.isNaN(parseFloat(formattedAmounts[Field.OUTPUT]))) ? (parseFloat(formattedAmounts[Field.OUTPUT])*1000000000)+"" : ""}
              showMaxButton={false}
              currency={initialInputCurrency}
              fiatValue={undefined}
              otherCurrency={currencies[Field.OUTPUT]}
              id={InterfaceSectionName.CURRENCY_INPUT_PANEL}
              loading={independentField === Field.OUTPUT }
              ref={inputCurrencyNumericalInputRef}
            />
        </SwapSection>
        <ArrowWrapper clickable={false}>
          
            <ArrowContainer
              data-testid="swap-currency-button"
              onClick={() => {}}
              color={theme.neutral1}
            >
              <ArrowDown size="16" color={theme.neutral1} />
            </ArrowContainer>
          
        </ArrowWrapper>
      </div>
      <AutoColumn gap="xs">
        <div>
          <OutputSwapSection>
              <SwapCurrencyInputPanel
                value={formattedAmounts[Field.OUTPUT]}
                disabled={disableTokenInputs}
                onUserInput={handleTypeOutput}
                label={<Trans>Mint</Trans>}
                showMaxButton={false}
                hideBalance={false}
                fiatValue={undefined}
                priceImpact={undefined}
                currency={initialOutputCurrency}
                otherCurrency={currencies[Field.INPUT]}
                id={InterfaceSectionName.CURRENCY_OUTPUT_PANEL}
                loading={independentField === Field.INPUT}
                numericalInputSettings={{
                  // We disable numerical input here if the selected token has tax, since we cannot guarantee exact_outputs for FOT tokens
                  disabled: inputTokenHasTax || outputTokenHasTax,
                  // Focus the input currency panel if the user tries to type into the disabled output currency panel
                  onDisabledClick: () => inputCurrencyNumericalInputRef.current?.focus(),
                }}
              />
          </OutputSwapSection>
        </div>

        <div>
          {switchingChain ? (
            <ButtonPrimary $borderRadius="16px" disabled={true}>
              <Trans>Connecting to {{ label: switchingChainIsSupported ? CHAIN_INFO[targetChain]?.label : '' }}</Trans>
            </ButtonPrimary>
          ) : isDisconnected ? (
            
              <ButtonLight onClick={toggleWalletDrawer} fontWeight={535} $borderRadius="16px">
                <Trans>Connect wallet</Trans>
              </ButtonLight>
          ) : chainId && chainId !== connectedChainId ? (
            <ButtonPrimary
              $borderRadius="16px"
              onClick={async () => {
                try {
                  await selectChain(chainId)
                } catch (error) {
                  if (didUserReject(error)) {
                    // Ignore error, which keeps the user on the previous chain.
                  } else {
                    // TODO(WEB-3306): This UX could be improved to show an error state.
                    throw error
                  }
                }
              }}
            >
              Connect to {isSupportedChain ? CHAIN_INFO[chainId].label : ''}
            </ButtonPrimary>
         
          ) : (
           
              <ButtonError
                onClick={() => { handleContinueToReview()}}
                //onClick={() => { }}
                id="swap-button"
                data-testid="swap-button"
                disabled={swapInputError? true : false}
                //error={!swapInputError && allowance.state === AllowanceState.ALLOWED} // remove the red color "critical" from the "mint" button due to our custom allowance/permit modification
              >
                <Text fontSize={20}>
                  {swapInputError ? (
                    swapInputError
                  ) : (
                    <Trans>Mint</Trans>
                  )}
                </Text>
              </ButtonError>
          )}
      
          
        </div>
      </AutoColumn>
    </>
  )
}

